import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { isSafeNextPath } from '@/consumer/lib/post-auth'
import { ArrowRight } from 'lucide-react'
import { toastApiError } from '@/shared/lib/api-error'
import { z } from 'zod'
import { Button } from '@/shared/components/ui/button'
import { Logo } from '@/shared/components/ui/logo'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { useConsumerBranding } from '@/consumer/hooks/use-consumer-branding'
import {
  useConsumerSession,
  useUpdateConsumerProfile,
} from '@/consumer/hooks/use-consumer-session'
type OnboardingValues = {
  first_name: string
  last_name: string
  accept_terms: boolean
}

export default function OnboardingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: session } = useConsumerSession()
  const { data: branding } = useConsumerBranding()
  const updateProfile = useUpdateConsumerProfile()
  const [logoError, setLogoError] = useState(false)
  const alreadyAcceptedTerms = !!session?.accepted_terms_at
  const defaultValues = useMemo<OnboardingValues>(
    () => ({
      first_name: session?.first_name ?? '',
      last_name: session?.last_name ?? '',
      accept_terms: alreadyAcceptedTerms,
    }),
    [session?.first_name, session?.last_name, alreadyAcceptedTerms],
  )
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'first_name',
        label: 'First name',
        placeholder: 'Jane',
        value: defaultValues.first_name,
        autoFocus: true,
        validationSchema: z.string().trim().min(1, 'First name is required'),
      },
      {
        type: 'text',
        name: 'last_name',
        label: 'Last name',
        placeholder: 'Appleseed',
        value: defaultValues.last_name,
        validationSchema: z.string().trim().min(1, 'Last name is required'),
      },
      // Only ask for acceptance if we have not already recorded it. Once
      // accepted, the consumer never sees the checkbox again.
      ...(alreadyAcceptedTerms
        ? []
        : [
            {
              type: 'checkbox' as const,
              name: 'accept_terms',
              label: 'I have read and accept the Terms & Conditions and Privacy Policy',
              value: false,
              validationSchema: z
                .boolean()
                .refine((v) => v === true, 'You must accept the terms to continue'),
            },
          ]),
    ],
    [defaultValues.first_name, defaultValues.last_name, alreadyAcceptedTerms],
  )
  const { Form } = useForm(
    {
      name: 'consumer-onboarding-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as OnboardingValues
      try {
        await updateProfile.mutateAsync({
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          ...(alreadyAcceptedTerms ? {} : { accept_terms: true }),
        })
        const next = searchParams.get('next')
        navigate(isSafeNextPath(next) ? next : '/', { replace: true })
      } catch (err) {
        toastApiError(err, 'Something went wrong. Please try again.')
      }
    },
    [session?.first_name, session?.last_name, alreadyAcceptedTerms],
  )
  const brandingMode = branding?.branding_mode ?? 'GENERIC'
  const brandedLogoUrl = branding?.org_logo_url
  const brandedOrgName = branding?.org_name ?? 'Organisation'
  const hasBrandedLogo =
    (brandingMode === 'COBRANDED' || brandingMode === 'BRANDED') &&
    !!brandedLogoUrl &&
    !logoError

  function renderOrgLogo(height = 'h-8', maxWidth = 'max-w-[200px]', className = '') {
    if (!brandedLogoUrl) return null
    return (
      <img
        src={brandedLogoUrl}
        alt={brandedOrgName}
        className={`${height} ${maxWidth} object-contain ${className}`.trim()}
        onError={() => setLogoError(true)}
      />
    )
  }

  function renderDesktopLogo() {
    if (brandingMode === 'BRANDED' && hasBrandedLogo) {
      return renderOrgLogo('h-8', 'max-w-[200px]')
    }
    return <Logo size="lg" variant="white" />
  }

  function renderMobileLogo() {
    if (hasBrandedLogo) {
      return (
        <div className="flex flex-col gap-3">
          {renderOrgLogo('h-7', 'max-w-[160px]')}
          {brandingMode === 'COBRANDED' && (
            <div className="flex items-center gap-2 text-[10px] text-[var(--ink-3)]">
              <span>Powered by</span>
              <Logo size="sm" />
            </div>
          )}
        </div>
      )
    }

    return <Logo size="lg" />
  }

  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Left panel - branding */}
      <div className="hidden flex-col justify-between bg-[var(--green)] p-12 lg:flex lg:w-[45%]">
        <div>{renderDesktopLogo()}</div>

        <div>
          <h1 className="mb-4 font-display text-[44px] leading-[1.05] text-white">
            Welcome to
            <br />
            iWarranty.
          </h1>
          <p className="text-white/70 text-[13px] leading-relaxed max-w-md">
            {t(
              'onboarding.welcomeSubtitle',
              'Your product is already waiting in your wallet. Just confirm a few details to finish setting up your account.',
            )}
          </p>
        </div>

        <div className="flex items-center gap-6 text-[10px] uppercase tracking-[0.12em] text-white/40">
          <span>Secure</span>
          <span>·</span>
          <span>Your warranties, organised</span>
        </div>
      </div>
      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="mb-10 lg:hidden">{renderMobileLogo()}</div>

          <h2 className="mb-1 font-display text-[32px] text-[var(--ink)]">
            Finish signing up
          </h2>
          <p className="mb-8 text-[12px] text-[var(--ink-3)]">
            {session?.email
              ? `Signed in as ${session.email}.`
              : 'Let us know who you are.'}
          </p>

          <Form className="flex flex-col gap-5" />
          {!alreadyAcceptedTerms && (
            <p className="mt-3 text-[11px] leading-relaxed text-[var(--ink-3)]">
              Read our{' '}
              <Link
                to="/terms-and-conditions"
                target="_blank"
                rel="noreferrer"
                className="text-[var(--green)] underline underline-offset-2 hover:text-[var(--green-hover)]"
              >
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noreferrer"
                className="text-[var(--green)] underline underline-offset-2 hover:text-[var(--green-hover)]"
              >
                Privacy Policy
              </Link>
              .
            </p>
          )}
          <Button
            type="submit"
            form="consumer-onboarding-form"
            disabled={updateProfile.isPending}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-[4px] bg-[var(--green)] px-4 py-2.5 text-[12px] tracking-[0.06em] text-white transition-colors hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {updateProfile.isPending ? (
              'Saving…'
            ) : (
              <>
                {t('onboarding.continueButton')}
                <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
